import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { COLUMNS, COL, fmtEuro, fmtFull, rgba, normHandle } from "./crmUtils";

export function Drawer({ onClose, children, testid }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-ink/25 backdrop-blur-[2px]" onClick={onClose}>
      <aside data-testid={testid} onClick={(e) => e.stopPropagation()} className="fixed bottom-0 right-0 top-0 flex w-full max-w-[440px] flex-col overflow-y-auto bg-cream shadow-2xl">{children}</aside>
    </div>, document.body);
}

function History({ card }) {
  let ns = 0;
  const items = card.history.map((h, i) => {
    let label = "", color = "#9A8F8A";
    if (h.type === "move") {
      const c = COL[h.column];
      color = c?.color || color;
      if (h.column === "noshow") ns += 1;
      label = `${i === 0 ? "Carte créée dans" : h.auto ? "Passée automatiquement dans" : "Entrée dans"} « ${c?.title || h.column} »${h.column === "noshow" ? ` (no-show n°${ns})` : ""}`;
    } else if (h.type === "relance") { color = COL[h.column]?.color || color; label = `Relance faite${COL[h.column] ? ` (${COL[h.column].title})` : ""}`; }
    else if (h.type === "status") { color = h.active ? COL.gagne.color : color; label = h.active ? "Client repassé en actif" : "Client passé en inactif"; }
    return <li key={i} className="flex gap-2.5 text-[12.5px]"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: color }} /><div>{label}<p className="text-[11px] text-ink-muted">{fmtFull(h.at)}</p></div></li>;
  });
  return <ul className="mt-2 space-y-2.5">{items.reverse()}</ul>;
}

const inputCls = "w-full rounded-xl border border-line bg-white px-3 py-2 text-sm outline-none focus:border-bordeaux disabled:bg-cream";
const Label = ({ children }) => <span className="mb-1 block text-xs font-medium text-ink-muted">{children}</span>;

export default function CrmPanel({ card, ro, update, move, onDelete, onClose }) {
  const col = COL[card.column];
  const set = (patch) => update((s) => ({ ...s, cards: s.cards.map((x) => (x.id === card.id ? { ...x, ...patch } : x)) }));
  const c = card.client;
  return (
    <Drawer onClose={onClose} testid="crm-panel">
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: rgba(col.color, 0.14), color: col.color }}><span className="h-2 w-2 rounded-full" style={{ background: col.color }} />{col.title}</span>
        <button data-testid="crm-panel-close" onClick={onClose} className="rounded-full p-2 text-ink-muted hover:bg-bordeaux-tint hover:text-bordeaux"><X size={18} /></button>
      </div>
      <div className="grid gap-4 p-5">
        <label><Label>@ Instagram</Label><input data-testid="crm-f-ig" disabled={ro} className={inputCls} defaultValue={card.instagram} onChange={(e) => { const h = normHandle(e.target.value); if (h) set({ instagram: h }); }} onBlur={(e) => { if (!normHandle(e.target.value)) e.target.value = card.instagram; }} /></label>
        <label><Label>Lien de son Instagram</Label><input data-testid="crm-f-link" disabled={ro} className={inputCls} defaultValue={card.link} placeholder="https://instagram.com/…" onChange={(e) => set({ link: e.target.value.trim() })} /></label>
        <label><Label>Colonne</Label>
          <select data-testid="crm-f-move" disabled={ro} className={inputCls} value={card.column} onChange={(e) => move(card.id, e.target.value)}>{COLUMNS.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</select>
        </label>
        {c && (
          <div><Label>Client</Label>
            <div className="grid gap-0.5 rounded-xl border border-line bg-white px-3 py-2.5 text-[12.5px]">
              <p><span className="text-ink-muted">Montant de l'opportunité :</span> {fmtEuro(c.price)}</p>
              {c.signedAt && <p><span className="text-ink-muted">Signé le :</span> {fmtFull(c.signedAt)}</p>}
            </div>
          </div>
        )}
        <label><Label>Notes</Label><textarea data-testid="crm-f-notes" disabled={ro} rows={9} className={`${inputCls} resize-y leading-relaxed`} defaultValue={card.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Notes de conversation, notes d'appel de vente…" /></label>
        <div><p className="text-sm font-semibold text-ink">Historique</p><History card={card} /></div>
        {!ro && <button data-testid="crm-delete" onClick={onDelete} className="rounded-full border border-line bg-white py-2 text-sm font-medium text-ink-soft transition-colors hover:border-bordeaux hover:text-bordeaux">Supprimer la carte</button>}
      </div>
    </Drawer>
  );
}
